import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ISyncEventWithCalendarEntry, NotificationEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';

export class CalendarEntryAbortedEvent extends NotificationEvent implements ISyncEventWithCalendarEntry {
  static readonly TYPE_ID: string = '2e80a18d13494007b237e34a2882d6b7';
  static readonly SCHEMA_VERSION = 2;

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly seen = false,
      public readonly calendarEntry: CalendarEntry,
      public readonly calendarEntryId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return CalendarEntryAbortedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CalendarEntryAbortedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarEntryAbortedEvent {
    return new CalendarEntryAbortedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'calendarEntry')),
        MapUtils.get<string>(map, 'calendarEntryId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'calendarEntryId': this.calendarEntryId,
      'calendarEntry': context.toData(this.calendarEntry),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new CalendarEntryAbortedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.calendarEntry.clone(),
        this.calendarEntryId,
    ) as this;
  }

  isSame(other: CalendarEntryAbortedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.calendarEntryId === this.calendarEntryId &&
        isSameData(other.calendarEntry, this.calendarEntry) &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new CalendarEntryAbortedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.calendarEntry.clone(),
        this.calendarEntryId,
    ) as this;
  }
}

export class CalendarEntryAbortedEventBuilder extends DataModelBuilder<CalendarEntryAbortedEvent> {
  build(context: SerializationContext, map: SerializedData): CalendarEntryAbortedEvent {
    return CalendarEntryAbortedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarEntryAbortedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarEntryAbortedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
